import { useRef, useState, useEffect } from 'react';
import { ContentCopyIcon, CheckIcon } from '@/components/icons';
import { IconButton, useTheme } from '@mui/material';
import { scaledFontSize } from './createWidgetTheme';
import { useWidgetI18n } from '../i18n/WidgetI18nProvider';

interface CopyMessageButtonProps {
  text: string;
  disabled?: boolean;
}

export function CopyMessageButton({ text, disabled = false }: CopyMessageButtonProps) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCopy = async () => {
    if (disabled) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard write can fail in insecure contexts; silently degrade */
    }
  };

  const iconSize = scaledFontSize(theme, 14);

  return (
    <IconButton
      size="small"
      lang={locale}
      aria-label={copied ? t('feedback.copied') : t('feedback.copyMessage')}
      title={copied ? t('feedback.copiedTitle') : t('feedback.copyMessage')}
      disabled={disabled}
      onClick={handleCopy}
      sx={{
        color: copied ? 'success.main' : 'text.disabled',
        p: 0.5,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {copied
        ? <CheckIcon sx={{ fontSize: iconSize }} aria-hidden />
        : <ContentCopyIcon sx={{ fontSize: iconSize }} aria-hidden />}
    </IconButton>
  );
}

export default CopyMessageButton;
